import {isLoaded as isLoadedStatus, Status, UnassignedStatus} from 'ApplicationEntry/models/AWDStatus/Status';
import {UnassignedUnderwriter, Underwriter} from 'ApplicationEntry/models/AWDStatus/Underwriter';
import {isLoaded as isLoadedWorkType, UnassignedWorkType, WorkType} from 'ApplicationEntry/models/AWDStatus/WorkType';
import {Loaded} from 'Core/models/Async';
import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {UpdateWork} from 'ApplicationEntry/models/AWDStatus/UpdateWork';

type AWDStatusProps = {
  workType: StringUnion;
  workType2: StringUnion;
  workType3: StringUnion;
  status: StringUnion;
  status2: StringUnion;
  status3: StringUnion;
  underwriter: StringUnion;
  underwriter2: StringUnion;
  underwriter3: StringUnion;
  comments: StringUnion;
  comments2: StringUnion;
  comments3: StringUnion;
  override: boolean;
  override2: boolean;
  override3: boolean;
  workTypeList: WorkType;
  statusList: Status;
  underwriterList: Underwriter;
};
export class InvalidGroupAWDStatus {
  public readonly key = 'InvalidGroupAWDStatus';

  constructor(
    public readonly workType: StringUnion,
    public readonly workType2: StringUnion,
    public readonly workType3: StringUnion,
    public readonly status: StringUnion,
    public readonly status2: StringUnion,
    public readonly status3: StringUnion,
    public readonly underwriter: StringUnion,
    public readonly underwriter2: StringUnion,
    public readonly underwriter3: StringUnion,
    public readonly comments: StringUnion,
    public readonly comments2: StringUnion,
    public readonly comments3: StringUnion,
    public readonly override: boolean,
    public readonly override2: boolean,
    public readonly override3: boolean,
    public readonly workTypeList: WorkType,
    public readonly statusList: Status,
    public readonly underwriterList: Underwriter
  ) {}
}

export class ValidGroupAWDStatus {
  public static createApp({
    workType,
    workType2,
    workType3,
    status,
    status2,
    status3,
    underwriter,
    underwriter2,
    underwriter3,
    comments,
    comments2,
    comments3,
    override,
    override2,
    override3,
    workTypeList,
    statusList,
    underwriterList,
  }: AWDStatusProps) {
    return this.create(
      workType,
      workType2,
      workType3,
      status,
      status2,
      status3,
      underwriter,
      underwriter2,
      underwriter3,
      comments,
      comments2,
      comments3,
      override,
      override2,
      override3,
      workTypeList,
      statusList,
      underwriterList
    );
  }

  public static create(
    workType: StringUnion,
    workType2: StringUnion,
    workType3: StringUnion,
    status: StringUnion,
    status2: StringUnion,
    status3: StringUnion,
    underwriter: StringUnion,
    underwriter2: StringUnion,
    underwriter3: StringUnion,
    comments: StringUnion,
    comments2: StringUnion,
    comments3: StringUnion,
    override: boolean,
    override2: boolean,
    override3: boolean,
    workTypeList: WorkType,
    statusList: Status,
    underwriterList: Underwriter
  ) {
    return isLoadedWorkType(workTypeList) &&
      isNonEmptyString(workType) &&
      isLoadedStatus(statusList) &&
      isNonEmptyString(status)
      ? new ValidGroupAWDStatus(
          workType,
          workType2,
          workType3,
          status,
          status2,
          status3,
          underwriter,
          underwriter2,
          underwriter3,
          comments,
          comments2,
          comments3,
          override,
          override2,
          override3,
          workTypeList,
          statusList,
          underwriterList
        )
      : new InvalidGroupAWDStatus(
          workType,
          workType2,
          workType3,
          status,
          status2,
          status3,
          underwriter,
          underwriter2,
          underwriter3,
          comments,
          comments2,
          comments3,
          override,
          override2,
          override3,
          workTypeList,
          statusList,
          underwriterList
        );
  }

  public readonly key = 'ValidGroupAWDStatus';
  private constructor(
    public readonly workType: StringUnion,
    public readonly workType2: StringUnion,
    public readonly workType3: StringUnion,
    public readonly status: StringUnion,
    public readonly status2: StringUnion,
    public readonly status3: StringUnion,
    public readonly underwriter: StringUnion,
    public readonly underwriter2: StringUnion,
    public readonly underwriter3: StringUnion,
    public readonly comments: StringUnion,
    public readonly comments2: StringUnion,
    public readonly comments3: StringUnion,
    public readonly override: boolean,
    public readonly override2: boolean,
    public readonly override3: boolean,
    public readonly workTypeList: Loaded<string[]>,
    public readonly statusList: Loaded<string[]>,
    public readonly underwriterList: Underwriter
  ) {}
}

export type GroupAWDStatus = ValidGroupAWDStatus | InvalidGroupAWDStatus;

export type GroupAWDStatusOptions<TReturn> = ToPartialMatchOptions<GroupAWDStatus, 'key', TReturn>;

export const matchGroupAWDStatus = <TReturn>(
  awdStatus: GroupAWDStatus,
  options: GroupAWDStatusOptions<TReturn>
): TReturn => {
  switch (awdStatus.key) {
    case 'InvalidGroupAWDStatus':
      return options.InvalidGroupAWDStatus ? options.InvalidGroupAWDStatus(awdStatus) : options.default(awdStatus);
    case 'ValidGroupAWDStatus':
      return options.ValidGroupAWDStatus ? options.ValidGroupAWDStatus(awdStatus) : options.default(awdStatus);
  }
};

export const changeWorkType = (workType: string) => (app: GroupAWDStatus) =>
  ValidGroupAWDStatus.createApp({...app, workType: NonEmptyString.create(workType)});
export const changeWorkType2 = (workType2: string) => (app: GroupAWDStatus) =>
  ValidGroupAWDStatus.createApp({...app, workType2: NonEmptyString.create(workType2)});
export const changeWorkType3 = (workType3: string) => (app: GroupAWDStatus) =>
  ValidGroupAWDStatus.createApp({...app, workType3: NonEmptyString.create(workType3)});
export const changeStatus = (status: string) => (app: GroupAWDStatus) =>
  ValidGroupAWDStatus.createApp({...app, status: NonEmptyString.create(status)});
export const changeStatus2 = (status2: string) => (app: GroupAWDStatus) =>
  ValidGroupAWDStatus.createApp({...app, status2: NonEmptyString.create(status2)});
export const changeStatus3 = (status3: string) => (app: GroupAWDStatus) =>
  ValidGroupAWDStatus.createApp({...app, status3: NonEmptyString.create(status3)});
export const changeUnderwriter = (underwriter: string) => (app: GroupAWDStatus) =>
  ValidGroupAWDStatus.createApp({...app, underwriter: NonEmptyString.create(underwriter)});
export const changeUnderwriter2 = (underwriter2: string) => (app: GroupAWDStatus) =>
  ValidGroupAWDStatus.createApp({...app, underwriter2: NonEmptyString.create(underwriter2)});
export const changeUnderwriter3 = (underwriter3: string) => (app: GroupAWDStatus) =>
  ValidGroupAWDStatus.createApp({...app, underwriter3: NonEmptyString.create(underwriter3)});
export const changeComments = (comments: string) => (app: GroupAWDStatus) => {
  return ValidGroupAWDStatus.createApp({...app, comments: NonEmptyString.create(comments)});
};
export const changeComments2 = (comments2: string) => (app: GroupAWDStatus) => {
  return ValidGroupAWDStatus.createApp({...app, comments2: NonEmptyString.create(comments2)});
};
export const changeComments3 = (comments3: string) => (app: GroupAWDStatus) => {
  return ValidGroupAWDStatus.createApp({...app, comments3: NonEmptyString.create(comments3)});
};
export const changeOverride = () => (app: GroupAWDStatus) =>
  ValidGroupAWDStatus.createApp({...app, override: !app.override});
export const changeOverride2 = () => (app: GroupAWDStatus) =>
  ValidGroupAWDStatus.createApp({...app, override2: !app.override2});
export const changeOverride3 = () => (app: GroupAWDStatus) =>
  ValidGroupAWDStatus.createApp({...app, override3: !app.override3});
export const changeWorkTypeList = (workTypeList: WorkType) => (app: GroupAWDStatus) =>
  ValidGroupAWDStatus.createApp({...app, workTypeList});
export const changeStatusList = (statusList: Status) => (app: GroupAWDStatus) =>
  ValidGroupAWDStatus.createApp({...app, statusList});
export const changeUnderwriterList = (underwriterList: Underwriter) => (app: GroupAWDStatus) =>
  ValidGroupAWDStatus.createApp({...app, underwriterList});

export const resetApp = () => (app: GroupAWDStatus) =>
  ValidGroupAWDStatus.createApp({
    ...app,
    workTypeList: new UnassignedWorkType(),
    statusList: new UnassignedStatus(),
    underwriterList: new UnassignedUnderwriter(),
    workType: new EmptyString(),
    status: new EmptyString(),
    underwriter: new EmptyString(),
    override: false,
    comments: new EmptyString(),
  });

export const EmptyGroupAWDStatus = new InvalidGroupAWDStatus(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  false,
  false,
  new UnassignedWorkType(),
  new UnassignedStatus(),
  new UnassignedUnderwriter()
);
